import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../api/client';
import RangeToolbar from './RangeToolbar';
import './WorkReportTable.css';
import './PackersReport.css';

// Packers Report (Work Performance): the day layout from Production Plan → Assignments,
// one row per employee × product (or activity), with the operator's ticks, shift time
// and notes. Ticks / notes save on change (PUT /api/work-reports/packers/:id); shift
// time entered here is written to Daily Reports (workperformance) and shown from there.

const FLAGS = [
  ['plan_completed', 'Plan Completed'], ['photo_submitted', 'Photo Submitted'], ['damaged_bags_reported', 'Damaged Bags Reported'],
  ['hours_reported', 'Working Hours Reported'], ['scans_match', 'Scans Match?'],
];
const errMsg = (e, fallback) => (e.response && e.response.data && e.response.data.error) || fallback;
const fmtLong = iso => new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
const fmt12 = t => {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};
const fmtG = g => (g == null ? '' : `${Number(g).toFixed(2).replace(/\.?0+$/, '')}G`);
const boxesOf = (bags, bpb) => {
  if (bags == null || !bpb) return '';
  const b = Number(bags) / Number(bpb);
  return Number.isInteger(b) ? String(b) : b.toFixed(2);
};

const PackersReport = ({ search = '', employeeId = '' }) => {
  const [range, setRange] = useState(null); // { from, to } from RangeToolbar
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  // page-level search / employee filter (WorkPerformancePage)
  const visible = useMemo(() => (rows || []).filter(r =>
    (!employeeId || String(r.employee_id) === String(employeeId)) &&
    (!search || [r.employee, r.product, r.station_label, r.activity, r.report_notes, r.comment].some(v => v && String(v).toLowerCase().includes(search)))
  ), [rows, search, employeeId]);

  const load = useCallback(async () => {
    if (!range) return;
    setErr(null);
    try {
      const r = await api.get('/api/work-reports/packers', { params: range });
      setRows(r.data.rows || []);
    } catch (e) { setErr(errMsg(e, 'Failed to load the report')); setRows([]); }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => {
    const m = new Map();
    for (const r of visible) { if (!m.has(r.date)) m.set(r.date, []); m.get(r.date).push(r); }
    return [...m.entries()].map(([date, list]) => ({ date, rows: list })); // rows arrive date DESC
  }, [visible]);

  const patch = useCallback(async (id, body) => {
    try {
      const r = await api.put(`/api/work-reports/packers/${id}`, body);
      setRows(rs => {
        const me = rs.find(x => x.id === id);
        return rs.map(x => {
          if (x.id === id) return { ...x, ...r.data };
          // shift time / break belong to the employee's day: mirror it into their other rows of that day + shift
          if (r.data.daily_report && me && x.employee_id === me.employee_id && x.date === me.date && x.shift_id === me.shift_id) return { ...x, daily_report: r.data.daily_report };
          return x;
        });
      });
    } catch (e) { setErr(errMsg(e, 'Failed to save')); load(); }
  }, [load]);

  return (
    <div className="wrt-root">
      <RangeToolbar presets={[1, 2, 4, 8]} initialWeeks={2} onChange={setRange}
                    right={<span className="pkr-muted">{rows === null ? 'Loading…' : `${visible.length} rows`}</span>} />
      {err && <div className="pkr-err">{err}</div>}
      {rows && !visible.length && <div className="wrt-empty">{rows.length ? 'Nothing matches the search / employee filter' : 'No assignments in this period — fill the day in Production Plan → Assignments.'}</div>}
      {days.map(d => {
        const prod = d.rows.filter(r => r.mbsku), done = prod.filter(r => Number(r.plan_completed));
        return (
          <div className="wrt-week" key={d.date}>
            <div className="wrt-week__header pkr-day-head">
              <i className="fas fa-calendar-alt wrt-cal-icon" />
              <span className="wrt-week__label">{fmtLong(d.date)}</span>
              <span className="wrt-week__stats">
                <span className="wrt-badge wrt-badge--blue">{d.rows.length} rows</span>
                {prod.length > 0 && <span className={`wrt-badge ${done.length === prod.length ? 'wrt-badge--green' : 'wrt-badge--blue'}`}>{done.length}/{prod.length} plan completed</span>}
              </span>
            </div>
            <div className="pkr-scroll">
              <table className="pkr-table">
                <thead>
                  <tr>
                    <th>Shift</th><th>Employee</th><th>Station</th><th className="pkr-num">NO.</th><th>Product</th><th>LB/OZ to G</th>
                    <th className="pkr-num">Bags/box</th><th className="pkr-num">Bags</th><th className="pkr-num">Done</th><th className="pkr-num">Boxes</th>
                    {FLAGS.map(([k, l]) => <th key={k} className="pkr-chk pkr-flag">{l}</th>)}
                    <th>Shift time</th><th className="pkr-num">Break, min</th><th className="pkr-notes">Notes</th>
                  </tr>
                </thead>
                <tbody>{d.rows.map(r => <Row key={r.id} r={r} onPatch={patch} />)}</tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Row = ({ r, onPatch }) => {
  const isAct = !r.mbsku;
  const [notes, setNotes] = useState(r.report_notes || '');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [brk, setBrk] = useState('');   // break minutes, saved to Daily Reports together with the shift time
  useEffect(() => { setNotes(r.report_notes || ''); }, [r.report_notes]);
  const saveNotes = () => { if ((r.report_notes || '') !== notes) onPatch(r.id, { report_notes: notes }); };
  // Save as soon as both times are complete (onChange) — onBlur alone is unreliable with the
  // native time picker (no blur after picking, or the user reloads while the field still has focus).
  const saveTime = (s = start, e = end, b = brk) => { if (s && e) onPatch(r.id, { shift_start: s, shift_end: e, break_minutes: b === '' ? 0 : Number(b) }); };
  return (
    <tr className={isAct ? 'pkr-act' : ''}>
      <td className="pkr-muted" data-label="Shift">{r.shift || ''}</td>
      <td className="pkr-emp" data-label="Employee">{r.employee || ''}</td>
      <td data-label="Station">{r.station_label || r.activity || ''}</td>
      <td className="pkr-num" data-label="NO.">{r.product_no != null ? r.product_no : ''}</td>
      <td className="pkr-prod" data-label="Product" title={`${r.mbsku || ''}${r.brand ? ' · ' + r.brand : ''}`}>
        {r.product || ''}{r.brand && r.brand !== 'Organo Republic' && <span className="pkr-muted"> · {r.brand}</span>}
      </td>
      <td data-label="LB/OZ to G">{fmtG(r.grams)}</td>
      <td className="pkr-num" data-label="Bags/box">{r.bags_per_box != null ? r.bags_per_box : ''}</td>
      <td className="pkr-num" data-label="Bags">{r.qty_bags != null ? r.qty_bags : ''}</td>
      <td className="pkr-num pkr-done" data-label="Done">
        {isAct ? '' : <span className={r.done == null ? 'pkr-muted' : r.qty_bags != null && r.done >= r.qty_bags ? 'pkr-done--ok' : ''}
                  title={r.done == null ? 'No production record yet' : 'From production records (scanner)'}>{r.done == null ? '—' : r.done}</span>}
      </td>
      <td className="pkr-num" data-label="Boxes">{boxesOf(r.qty_bags, r.bags_per_box)}</td>
      {FLAGS.map(([k, l]) => (
        <td key={k} className="pkr-chk" data-label={l}>
          <input type="checkbox" checked={!!Number(r[k])} disabled={isAct && k !== 'hours_reported'} onChange={e => onPatch(r.id, { [k]: e.target.checked })} />
        </td>
      ))}
      <td className="pkr-time" data-label="Shift time">
        {r.daily_report
          ? <span title={`From Daily Reports (${r.daily_report.shift_name || 'S' + r.daily_report.shift})`}>{fmt12(r.daily_report.start)} – {fmt12(r.daily_report.end)}</span>
          : <span className="pkr-time-in" title="No Daily Report yet — enter start, end and break; they are saved to Daily Reports">
              <input type="time" value={start} onChange={e => { setStart(e.target.value); saveTime(e.target.value, end, brk); }} onBlur={() => saveTime()} /> –
              <input type="time" value={end} onChange={e => { setEnd(e.target.value); saveTime(start, e.target.value, brk); }} onBlur={() => saveTime()} />
            </span>}
      </td>
      <td className="pkr-num pkr-break-cell" data-label="Break, min">
        {r.daily_report
          ? <span className={r.daily_report.break > 0 ? '' : 'pkr-muted'}>{r.daily_report.break > 0 ? r.daily_report.break : '—'}</span>
          : <input type="number" min="0" step="5" className="pkr-break" placeholder="0" value={brk} title="Break, minutes — saved with the shift time" onChange={e => setBrk(e.target.value)} onBlur={() => saveTime()} />}
      </td>
      <td className="pkr-notes" data-label="Notes"><input type="text" value={notes} placeholder="Notes" onChange={e => setNotes(e.target.value)} onBlur={saveNotes} onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }} /></td>
    </tr>
  );
};

export default PackersReport;
